/* RESPONSIBLE TEAM: team-standalone */
import type AppService from 'embercom/services/app-service';
import Model, { attr } from '@ember-data/model';
import { post, get } from 'embercom/lib/ajax';

import { inject as service } from '@ember/service';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import { IntegrationState } from 'embercom/objects/standalone/constants';
import { action } from '@ember/object';
import { use } from 'ember-resources/util/function-resource';
import { AsyncData } from 'embercom/resources/utils/async-data';
import { taskFor } from 'ember-concurrency-ts';
import { isPresent } from '@ember/utils';

const API_ROOT = '/ember/standalone/salesforce_configurations';

export default class SalesforceConfiguration extends Model {
  @service declare finStandaloneService: FinStandaloneService;
  @service declare appService: AppService;

  @attr('string') declare instanceUrl: string;
  @attr('boolean') declare isSandbox: boolean;
  @attr('number') declare workflowRulesetId: number;
  @attr('string') declare organisationId: string | undefined;
  @attr('string') declare supportEmail: string | undefined;
  @attr('string') declare userId: string | undefined;
  @attr('string') declare identityMappingContactField: string;
  @attr('string') declare identityMappingAccountField: string | undefined;
  @attr('boolean', { defaultValue: false }) declare hasInstalledCopilot: boolean; // to be implemented

  @attr('string')
  declare finSalesforceUserId: string | undefined;

  @attr('number', { defaultValue: IntegrationState.NotConfigured })
  declare status: IntegrationState;

  @use ruleset = AsyncData<$TSFixMe>(async () => {
    let ruleset = await this.store.peekRecord('matching-system/ruleset', this.workflowRulesetId);

    if (!ruleset) {
      ruleset = await this.store.findRecord('matching-system/ruleset', this.workflowRulesetId);
    }

    let customBot = ruleset.rulesetLinks.firstObject.object;
    customBot.visualBuilderObject.editorConfig = customBot.config.editorConfig;
    return ruleset;
  });

  get isSuccessfullyConnected() {
    return this.isConnected && !this.isAuthFailed;
  }

  get hasFinishedCopilotSetup() {
    return this.hasInstalledCopilot && this.appService.app.humanAdminsWithCopilotSeat.length > 0;
  }

  get isConnected() {
    return !this.isPendingSetup;
  }

  get isPendingSetup() {
    return this.status === IntegrationState.NotConfigured;
  }

  get isEnabled() {
    return this.status === IntegrationState.Enabled;
  }

  get isPaused() {
    return this.status === IntegrationState.Paused;
  }

  get isAuthFailed() {
    return this.status === IntegrationState.AuthFailed;
  }

  get visualBuilderObject() {
    return this.ruleset.value?.rulesetLinks.firstObject?.object.visualBuilderObject;
  }

  get isWorkflowValid() {
    return Boolean(this.visualBuilderObject?.validations.isValid);
  }

  get legacyReadyToSetLive() {
    return this.isConnected && this.finSalesforceUserId && this.supportEmail;
  }

  get hasValidEmailConfiguration() {
    let hasCaseOrigins = isPresent(this.finStandaloneService.selectedSalesforceCaseOrigins);
    let hasVerifiedSupportEmail = isPresent(this.supportEmail) && this.isSupportDomainVerified;

    return this.appService.app.hasOptionalConfigureEmails
      ? !hasCaseOrigins || hasVerifiedSupportEmail
      : hasCaseOrigins && hasVerifiedSupportEmail;
  }

  get readyToSetLive() {
    return (
      this.isConnected &&
      isPresent(this.finSalesforceUserId) &&
      Boolean(this.finStandaloneService.salesforceSetupData?.hasPermissionSet) &&
      Boolean(this.finStandaloneService.salesforceSetupData?.hasPermissionSetAssignedToInstaller) &&
      this.hasValidEmailConfiguration &&
      this.isWorkflowValid
    );
  }

  get environmentLabel() {
    return this.isSandbox ? 'sandbox' : 'production';
  }

  get domain() {
    return this.instanceUrl.replace('https://', '');
  }

  get lightningForceUrl() {
    let instance = /https:\/\/(.*)\.my\.salesforce\.com/.exec(this.instanceUrl);

    if (instance) {
      return `https://${instance[1]}.lightning.force.com`;
    } else {
      return undefined;
    }
  }

  get dkimSettings() {
    return this.finStandaloneService.app.dkim_settings.reject((setting: any) =>
      setting.domain.endsWith('.hirefin.com'),
    );
  }

  get emailAddressHash() {
    let addressHash = new Map();
    this.finStandaloneService.app.customEmailAddresses.forEach((address: $TSFixMe) => {
      let domain = address.email
        .substring((address.email.lastIndexOf('@') as number) + 1)
        .toLowerCase();

      if (!addressHash.has(domain)) {
        addressHash.set(domain, []);
      }
      addressHash.get(domain).push({
        email: address.email,
        verified: address.verified,
        id: address.id,
      });
    });

    return addressHash;
  }

  get domainSettings() {
    let emailAddressHash = this.emailAddressHash;
    let domains: any[] = [];

    this.dkimSettings.forEach((record: any) => {
      let customBounceSetting = this.finStandaloneService.app.customBounceSettings?.find(
        (bounceSetting: any) => {
          return bounceSetting.rootDomain === record.domain;
        },
      );

      let authenticated =
        record.validRecordExists &&
        customBounceSetting?.validRecordExists &&
        record.validDmarcExists;

      domains.push({
        authenticated,
        dkimRecord: record,
        customBounceSetting,
        customAddresses: emailAddressHash.get(record.domain.toLowerCase()) || [],
      });
    });
    return domains;
  }

  get selectedSupportDomainSettings() {
    return this.domainSettings.find((domain: any) =>
      domain.customAddresses.any((address: any) => this.supportEmail === address.email),
    );
  }

  get isSupportDomainVerified() {
    return Boolean(this.selectedSupportDomainSettings?.authenticated);
  }

  get emailToCaseSettingsUrl() {
    return `https://${this.domain}/lightning/setup/EmailToCase/home`;
  }

  @action async getUsageCounts(): Promise<{
    involvement_count: number;
    last_active?: string;
  }> {
    return get(`${API_ROOT}/${this.id}/usage_counts`);
  }

  async setFinUser(finSalesforceUserId: string) {
    await post(`${API_ROOT}/${this.id}/set_fin_salesforce_user`, {
      fin_salesforce_user_id: finSalesforceUserId,
    });

    this.finSalesforceUserId = finSalesforceUserId;
  }

  async activateForCases() {
    await post(`${API_ROOT}/${this.id}/activate`);
    await this.reload();
  }

  async deactivateForCases() {
    await post(`${API_ROOT}/${this.id}/deactivate`);
    await this.reload();
  }

  async disconnectApp() {
    await post(`${API_ROOT}/${this.id}/disconnect`);
    this.instanceUrl = '';
    this.status = IntegrationState.NotConfigured;
    await taskFor(this.finStandaloneService.salesforceDataLoader.loadSalesforceSetupData).perform();
  }

  async setSupportEmail(supportEmail: string) {
    let response = await post(`${API_ROOT}/${this.id}/set_support_email`, {
      support_email: supportEmail,
    });
    await this.reload();
    return response;
  }
}
